import { useRef } from 'react'
import { VoiceRipple } from '@/components/chrome/Ripple'
import { Lang, PrismLight } from '@/components/ui'
import { manifesto } from '@/content/site'
import { useInView } from '@/hooks/useInView'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { EASE, gsap, ScrollTrigger, useGSAP } from '@/lib/gsap'
import styles from './Manifesto.module.css'

/**
 * The word behind the name: कर्ता set huge in Anek Devanagari, its weight breathing in
 * (wght axis only) as it arrives, over soft prism voice ripples, with the gloss
 * "kartā — the one who gets it done". A `<dl>`: the term and what it means.
 * The ripples breathe only while on screen; reduced motion shows the still composition.
 */
export function Karta() {
  const rootRef = useRef<HTMLDivElement>(null)
  const reducedMotion = useReducedMotion()
  const inView = useInView(rootRef, { rootMargin: '15% 0px' })

  useGSAP(
    () => {
      const root = rootRef.current
      if (!root) return
      const q = gsap.utils.selector(root)
      const word = q('[data-karta-word]')
      const gloss = q('[data-karta-gloss]')
      const ripple = q('[data-karta-ripple]')

      if (reducedMotion) {
        gsap.from([word, gloss], {
          opacity: 0,
          duration: 0.3,
          ease: 'none',
          scrollTrigger: { trigger: root, start: 'top 80%', once: true },
        })
        return
      }

      gsap.set(word, { opacity: 0, fontWeight: 140, scale: 0.965 })
      gsap.set(gloss, { opacity: 0, y: 18 })
      gsap.set(ripple, { opacity: 0, scale: 0.82 })
      const timeline = gsap
        .timeline({ paused: true })
        .to(ripple, { opacity: 1, scale: 1, duration: 3.2, ease: EASE.out }, 0)
        .to(word, { opacity: 1, duration: 1.6, ease: EASE.out }, 0.1)
        // The breath: a long, soft rise of weight. Only wght moves (no width axis).
        .to(word, { fontWeight: 560, duration: 3.4, ease: 'power2.out' }, 0.1)
        .to(word, { scale: 1, duration: 3.4, ease: EASE.out }, 0.1)
        .to(gloss, { opacity: 1, y: 0, duration: 1.5, ease: EASE.out }, 1.1)

      ScrollTrigger.create({
        trigger: root,
        start: 'top 68%',
        once: true,
        onEnter: () => timeline.play(),
      })
    },
    { scope: rootRef, dependencies: [reducedMotion], revertOnUpdate: true },
  )

  return (
    <div ref={rootRef} className={styles.karta}>
      <div className={styles.kartaLight} aria-hidden="true">
        <PrismLight placement="center" intensity={0.75} />
      </div>
      <dl className={styles.kartaTerm}>
        <dt className={styles.kartaWord}>
          {/* Centred on the word itself; outside the breathing span so it doesn't scale. */}
          <span className={styles.kartaRipple} data-karta-ripple aria-hidden="true">
            <VoiceRipple rings={5} period={7} playing={inView && !reducedMotion} />
          </span>
          <Lang code="hi" className={styles.kartaDeva} data-karta-word>
            {manifesto.karta.deva}
          </Lang>
        </dt>
        <dd className={styles.kartaGloss} data-karta-gloss>
          <Lang code="hi-Latn" className={styles.kartaLatin}>
            {manifesto.karta.latin}
          </Lang>
          <span className={styles.kartaDot} aria-hidden="true" />
          <span>{manifesto.karta.gloss}</span>
        </dd>
      </dl>
    </div>
  )
}
